import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from '../../components/ui'
import RedshiftDataSourcePanel from '../revenue-close/RedshiftDataSourcePanel'
import {
  downloadDeferredRevenueExport,
  useDeferredRevenueAgentReview,
  useDeferredRevenueAvailableConnectors,
  useDeferredRevenueDataSourceConfig,
  useDeferredRevenueHealth,
  useDeferredRevenueRun,
  useClearDeferredRevenueDataSource,
  useSetDeferredRevenueDataSource,
  useTestDeferredRevenueDataSource,
} from './api'
import type {
  AgentReview,
  DeferredRevenueHealth,
  DeferredRevenueRun,
  DeferredRevenueView,
  ExportStatus,
  RollForwardRow,
} from './types'
import { formatCurrency, hitlBadgeClass, severityBadgeClass } from './utils'

const DEFAULT_REDSHIFT_SCHEMA = 'deferred_revenue_scheduler'
const EXPECTED_REDSHIFT_TABLES = [
  'deferred_revenue_runs',
  'deferred_revenue_rolls',
  'deferred_revenue_journal_rows',
  'deferred_revenue_audit_trail',
  'deferred_revenue_source_files',
]

export default function DeferredRevenueSchedulerPage({ view = 'overview' }: { view?: DeferredRevenueView }) {
  const { data, isLoading, error } = useDeferredRevenueRun()
  const { data: health } = useDeferredRevenueHealth()

  if (view === 'sources') {
    return (
      <SourcesView
        run={data}
        health={health}
        isRunLoading={isLoading}
        hasRunError={Boolean(error)}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading deferred revenue schedule...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-[1180px] space-y-4">
        <DataSourceStrip health={health} />
        <DeferredRevenueDataSourceConfiguration health={health} />
        <Panel>
          <PanelBody>
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Deferred revenue data could not be loaded. Check the Close Calendar gate for blocking prerequisites.
            </p>
          </PanelBody>
        </Panel>
      </div>
    )
  }

  if (view === 'review') return <ReviewView run={data} health={health} />
  if (view === 'exports') return <ExportsView run={data} health={health} />
  return <OverviewView run={data} health={health} />
}

function DeferredRevenueDataSourceConfiguration({ health }: { health?: DeferredRevenueHealth }) {
  const { data: config, isLoading: configLoading } = useDeferredRevenueDataSourceConfig()
  const { data: connectors = [], isLoading: connectorsLoading } = useDeferredRevenueAvailableConnectors()
  const saveMutation = useSetDeferredRevenueDataSource()
  const clearMutation = useClearDeferredRevenueDataSource()
  const testMutation = useTestDeferredRevenueDataSource()

  return (
    <RedshiftDataSourcePanel
      appletName="Deferred Revenue Scheduler"
      defaultSchema={DEFAULT_REDSHIFT_SCHEMA}
      expectedTables={EXPECTED_REDSHIFT_TABLES}
      config={config}
      health={health}
      connectors={connectors}
      isLoading={configLoading || connectorsLoading}
      saveMutation={saveMutation}
      clearMutation={clearMutation}
      testMutation={testMutation}
    />
  )
}

function OverviewView({ run, health }: { run: DeferredRevenueRun; health?: DeferredRevenueHealth }) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1500px] space-y-6">
      <DataSourceStrip health={health} />
      <DecisionStrip run={run} />

      <div className="grid grid-cols-[minmax(0,1fr)_420px] gap-6 max-xl:grid-cols-1">
        <RollForwardTable rows={run.roll_forward} />
        <ReviewRail items={run.review_items} />
      </div>

      <div className="grid grid-cols-2 gap-6 max-2xl:grid-cols-1">
        <JournalTable rows={run.journal_entries} />
        <AuditTable rows={run.audit_trail.slice(0, 8)} />
      </div>
    </div>
  )
}

function DataSourceStrip({ health }: { health?: DeferredRevenueHealth }) {
  const sourceMode = health?.data_source_mode ?? health?.source_mode ?? 'unconfigured'
  let sourceLabel = sourceMode
  if (health?.redshift_connected) {
    sourceLabel = 'Redshift connected'
  } else if (sourceMode === 'unconfigured') {
    sourceLabel = 'Redshift required'
  }
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-dark-border dark:bg-dark-surface sm:px-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={health?.status === 'ok' ? 'success' : 'warning'}>
            Close package {health?.status === 'ok' ? 'available' : 'checking'}
          </Badge>
          <Badge variant={health?.redshift_connected ? 'success' : 'warning'}>
            {sourceLabel}
          </Badge>
          {health?.redshift_schema && (
            <Badge variant="secondary" className="inline-block max-w-full whitespace-normal break-all">
              schema {health.redshift_schema}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Badge variant={health?.langfuse_enabled ? 'success' : 'warning'}>
            Langfuse {health?.langfuse_enabled ? 'enabled' : 'inactive'}
          </Badge>
          <Badge variant="info">ASC 210 ST/LT</Badge>
        </div>
      </div>
      {health?.message && (
        <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
          {health.message}
        </p>
      )}
    </div>
  )
}

function DecisionStrip({ run }: { run: DeferredRevenueRun }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.3fr_0.7fr_0.7fr_0.7fr]">
        <div className="border-b border-gray-200 p-4 dark:border-dark-border sm:col-span-2 sm:p-5 xl:col-span-1 xl:border-b-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Deferred revenue decision
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="text-2xl font-semibold tracking-normal text-charcoal dark:text-gray-100 sm:text-3xl">
              {formatCurrency(run.summary.closing_balance_total, true)}
            </div>
            <Badge variant="warning">{run.summary.pending_review_count} review items</Badge>
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            {run.summary.policy} is ready once Gate 2 D-code rows are dispositioned and the 23900/23910 reclass is posted.
          </p>
        </div>
        <DecisionMetric label="Current" value={formatCurrency(run.summary.current_portion_total, true)} />
        <DecisionMetric label="Non-current" value={formatCurrency(run.summary.noncurrent_portion_total, true)} />
        <DecisionMetric label="Reclass JE" value={run.summary.journal_balanced ? 'Balanced' : 'Blocked'} />
      </div>
    </div>
  )
}

function DecisionMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-t border-gray-200 p-4 dark:border-dark-border sm:p-5 xl:border-l xl:border-t-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-2 break-words text-xl font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}

function RollForwardTable({ rows }: { rows: RollForwardRow[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Deferred revenue roll-forward</PanelTitle>
        <PanelDescription>
          Contract-level opening, billings, recognition, closing balance, and ST/LT split.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-[780px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Contract</th>
              <th className="px-4 py-3 text-right font-semibold">Closing</th>
              <th className="px-4 py-3 text-right font-semibold">Current</th>
              <th className="px-4 py-3 text-right font-semibold">Non-current</th>
              <th className="px-4 py-3 font-semibold">Disposition</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={row.roll_id} className="align-top">
                <td className="px-5 py-4">
                  <div className="font-semibold text-charcoal dark:text-gray-100">
                    {row.customer}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {row.contract_id} - {row.segment}
                  </div>
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.closing_balance)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.current_portion_st)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.noncurrent_portion_lt)}
                </td>
                <td className="px-4 py-4">
                  <span className={hitlBadgeClass(row.hitl_state)}>{row.disposition}</span>
                  <div className="mt-1 max-w-[240px] truncate text-xs text-gray-500 dark:text-gray-400">
                    {row.exception_type || row.awareness_flag || 'Routine'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ReviewRail({ items }: { items: RollForwardRow[] }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Gate 2 review queue</PanelTitle>
        <PanelDescription>
          D-code rows requiring Controller disposition before downstream tie-out.
        </PanelDescription>
      </PanelHeader>
      <PanelBody className="space-y-3">
        {items.map(item => (
          <div key={item.roll_id} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-charcoal dark:text-gray-100">
                  {item.roll_id} - {item.exception_type}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {item.contract_id} - {item.reviewer}
                </div>
              </div>
              <span className={hitlBadgeClass(item.hitl_state)}>{item.hitl_state}</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {item.rule_or_trigger}
            </p>
            <div className="mt-3 text-sm font-semibold tabular-nums text-charcoal dark:text-gray-100">
              {formatCurrency(item.reclass_je_amount)}
            </div>
          </div>
        ))}
      </PanelBody>
    </Panel>
  )
}

function JournalTable({ rows }: { rows: DeferredRevenueRun['journal_entries'] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>ST/LT reclass journal</PanelTitle>
        <PanelDescription>
          NetSuite-ready DR 23900 / CR 23910 rows for material non-current deferred revenue.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Reference</th>
              <th className="px-4 py-3 font-semibold">Account</th>
              <th className="px-4 py-3 text-right font-semibold">Debit</th>
              <th className="px-4 py-3 text-right font-semibold">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={`${row.je_external_id}-${row.line}`}>
                <td className="px-5 py-4">
                  <div className="font-medium text-charcoal dark:text-gray-100">{row.je_external_id}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{row.memo}</div>
                </td>
                <td className="px-4 py-4 text-gray-600 dark:text-gray-300">
                  {row.account}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.debit)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.credit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function AuditTable({ rows }: { rows: DeferredRevenueRun['audit_trail'] }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Audit trail sample</PanelTitle>
        <PanelDescription>
          Dual-state SOX evidence retained for deferred revenue classification.
        </PanelDescription>
      </PanelHeader>
      <PanelBody className="space-y-3">
        {rows.map(row => (
          <div key={row.audit_id} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold text-charcoal dark:text-gray-100">{row.audit_id}</div>
              <span className={hitlBadgeClass(row.hitl_state)}>{row.hitl_state}</span>
            </div>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {row.rule_or_trigger}
            </p>
          </div>
        ))}
      </PanelBody>
    </Panel>
  )
}

function ReviewView({ run, health }: { run: DeferredRevenueRun; health?: DeferredRevenueHealth }) {
  const reviewMutation = useDeferredRevenueAgentReview()
  const review = reviewMutation.data

  return (
    <div className="mx-auto w-full min-w-0 max-w-[1300px] space-y-6">
      <DataSourceStrip health={health} />
      <Panel>
        <PanelHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <PanelTitle>Agent review</PanelTitle>
              <PanelDescription>
                Deterministic EC-03 review across D-code routing, ST/LT split, GL D6 tie-out, and JE readiness.
              </PanelDescription>
            </div>
            <Button
              onClick={() => reviewMutation.mutate({ use_llm: false })}
              disabled={reviewMutation.isPending}
            >
              <ShieldCheckIcon className="mr-2 h-4 w-4" />
              Run Review
            </Button>
          </div>
        </PanelHeader>
        <PanelBody>
          {review ? <AgentReviewResult review={review} /> : (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
              Run the review to generate a Controller checklist for {run.summary.pending_review_count} Gate 2 item(s).
            </div>
          )}
        </PanelBody>
      </Panel>
      <ReviewRail items={run.review_items} />
    </div>
  )
}

function AgentReviewResult({ review }: { review: AgentReview }) {
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-semibold text-charcoal dark:text-gray-100">{review.agent_name}</div>
          <Badge variant={review.traced_with_langfuse ? 'success' : 'secondary'}>
            {review.traced_with_langfuse ? 'Langfuse traced' : 'Deterministic'}
          </Badge>
        </div>
        <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{review.summary}</p>
      </div>

      <div className="grid gap-3">
        {review.findings.map((finding, index) => (
          <div key={`${finding.title}-${index}`} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex flex-wrap items-center gap-2">
              <span className={severityBadgeClass(finding.severity)}>{finding.severity}</span>
              <span className="font-semibold text-charcoal dark:text-gray-100">{finding.title}</span>
            </div>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{finding.detail}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function SourcesView({
  run,
  health,
  isRunLoading = false,
  hasRunError = false,
}: {
  run?: DeferredRevenueRun
  health?: DeferredRevenueHealth
  isRunLoading?: boolean
  hasRunError?: boolean
}) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1180px] space-y-6">
      <DataSourceStrip health={health} />
      <DeferredRevenueDataSourceConfiguration health={health} />
      <Panel>
        <PanelHeader>
          <PanelTitle>Source evidence</PanelTitle>
          <PanelDescription>
            Feeders and self-state inputs used by the deferred revenue scheduler.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="grid gap-3">
          {isRunLoading ? (
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
              Loading deferred revenue source evidence...
            </div>
          ) : hasRunError || !run ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
              Deferred revenue source evidence could not be loaded. The Redshift connector can still be reviewed above.
            </div>
          ) : (
            run.source_files.map(file => (
              <div key={file.filename} className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 p-4 dark:border-dark-border">
                <div>
                  <div className="font-semibold text-charcoal dark:text-gray-100">{file.filename}</div>
                  <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">{file.description}</p>
                </div>
                {file.available ? (
                  <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <XCircleIcon className="h-5 w-5 shrink-0 text-rose-600 dark:text-rose-400" />
                )}
              </div>
            ))
          )}
        </PanelBody>
      </Panel>
    </div>
  )
}

function ExportsView({ run, health }: { run: DeferredRevenueRun; health?: DeferredRevenueHealth }) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1180px] space-y-6">
      <DataSourceStrip health={health} />
      <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1">
        <ExportCard
          title="Deferred revenue roll"
          exportStatus={run.exports.roll_forward}
          onDownload={() => downloadDeferredRevenueExport('roll')}
        />
        <ExportCard
          title="NetSuite JE import"
          exportStatus={run.exports.journal_entry}
          onDownload={() => downloadDeferredRevenueExport('journal')}
        />
        <ExportCard
          title="Audit trail"
          exportStatus={run.exports.audit_trail}
          onDownload={() => downloadDeferredRevenueExport('audit')}
        />
      </div>
    </div>
  )
}

function ExportCard({
  title,
  exportStatus,
  onDownload,
}: {
  title: string
  exportStatus: ExportStatus
  onDownload: () => void
}) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
        <PanelDescription>
          <span className="break-all">{exportStatus.filename}</span>
        </PanelDescription>
      </PanelHeader>
      <PanelBody className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Rows</div>
            <div className="mt-1 font-semibold text-charcoal dark:text-gray-100">{exportStatus.rows}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Balanced</div>
            <div className="mt-1 font-semibold text-charcoal dark:text-gray-100">
              {exportStatus.balanced ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
        <Button variant="secondary" className="w-full justify-center" onClick={onDownload}>
          <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
      </PanelBody>
    </Panel>
  )
}
